"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import {
    Book,
    Users,
    BarChart3,
    MessageCircleQuestion,
    Brain,
    Compass,
    Library,
    UsersRound,
    Trophy,
    UserRound,
} from "lucide-react"

export default function WhyChooseUs() {
    const [hoveredCard, setHoveredCard] = useState(null)

    const features = [
        {
            icon: <Book className="h-10 w-10" />,
            title: "Comprehensive Study Material",
            description:
                "Get access to top-quality notes and materials that cover the entire syllabus in a simplified manner.",
        },
        {
            icon: <Users className="h-10 w-10" />,
            title: "Expert Faculty",
            description: "Learn from experienced teachers who provide personalized attention to every student.",
        },
        {
            icon: <BarChart3 className="h-10 w-10" />,
            title: "Regular Tests & Performance Tracking",
            description:
                "We conduct frequent assessments with AI-powered analytics to track progress and suggest improvements.",
        },
        {
            icon: <MessageCircleQuestion className="h-10 w-10" />,
            title: "Doubt Clearing Sessions",
            description: "Our interactive doubt clearing sessions ensure that no student is left behind.",
        },
        {
            icon: <Brain className="h-10 w-10" />,
            title: "AI-Powered Smart Learning",
            description:
                "Personalized study recommendations, smart quizzes, and AI-driven performance analysis to boost learning efficiency.",
        },
        {
            icon: <Compass className="h-10 w-10" />,
            title: "Career Guidance & Mentorship",
            description: "Our dedicated mentors help students explore career options and choose the right academic path.",
        },
        {
            icon: <Library className="h-10 w-10" />,
            title: "Smart Digital Library",
            description: "Access a vast collection of e-books, video lectures, and reference materials anytime, anywhere.",
        },
        {
            icon: <UsersRound className="h-10 w-10" />,
            title: "Interactive Learning Environment",
            description:
                "Group discussions, peer-to-peer learning, and engaging activities make learning enjoyable and effective.",
        },
        {
            icon: <Trophy className="h-10 w-10" />,
            title: "Success-Oriented Approach",
            description:
                "With a structured curriculum, strategic study plans, and expert guidance, we pave the way for student success.",
        },
        {
            icon: <UserRound className="h-10 w-10" />,
            title: "Personalized Mentorship",
            description:
                "One-on-one mentorship programs to ensure students receive guidance tailored to their strengths and weaknesses.",
        },
    ]

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    }

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
    }

    return (
        <section className="py-16 px-4 md:px-8 bg-gradient-to-br from-white to-[#f0f9fb]">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl  font-semibold mb-4 relative inline-block">
                        Why Choose <span className="text-[#0097b2]">SSS Achievers</span>
                    </h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        Join a learning experience designed for today's students with cutting-edge technology and personalized
                        support.
                    </p>
                </div>

                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                >
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            className={`relative overflow-hidden rounded-xl p-6 transition-all duration-300 ${
                                hoveredCard === index
                                    ? "bg-[#0097b2] text-white shadow-lg scale-105 z-10"
                                    : index === 0 || index===9
                                        ? "bg-[#0097b2] text-white shadow-lg"
                                        : "bg-white shadow-md hover:shadow-lg"
                            }`}
                            onMouseEnter={() => setHoveredCard(index)}
                            onMouseLeave={() => setHoveredCard(null)}
                            variants={itemVariants}
                        >
                            {/* Top right curve with constant #f7bb0a color */}
                            <div
                                className="absolute top-0 right-0 w-20 h-20 -mr-8 -mt-8 rounded-full"
                                style={{ backgroundColor: "#f7bb0a" }}
                            ></div>

                            <div
                                className={`mb-4 text-[#0097b2] transition-colors duration-300 ${
                                    hoveredCard === index || index === 0   || index===9? "text-white" : ""
                                }`}
                            >
                                {feature.icon}
                            </div>

                            <h3 className="text-xl font-bold mb-2">{feature.title}</h3>

                            <p className={`text-sm ${
                                hoveredCard === index || index === 0 || index===9? "text-white/90" : "text-gray-600"
                            }`}>
                                {feature.description}
                            </p>

                            <div
                                className={`absolute bottom-0 left-0 h-1 transition-all duration-300 ${
                                    hoveredCard === index || index === 0 || index===9
                                        ? "bg-[#f7bb0a] w-full"
                                        : "bg-[#0097b2] w-1/3"
                                }`}
                            ></div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    )
}
